import { useCallback, useEffect, useRef, useState } from 'react';
import { preferenceStorage } from '../../consent/consent.js';
import { useLocale, useT } from '../../i18n/index.js';
import { LandingIcon } from '../../ui/LandingIcon.js';
import { useHydrated } from '../../lib/hydration.js';
import { Mascot3D } from './Mascot3D.js';

/**
 * Magix in the corner of the page, saying one sentence as each part scrolls in.
 *
 * It is a guide, not a chat: four sentences, one per section, each shown once
 * and only on the way down, then folded away after a few seconds so the face
 * is all that is left in the corner. It never shows over the hero, which says
 * the same things at a hundred times the size, nor over the footer's links, and once it is closed it does
 * not come back for that visitor. A phone has no corner to spare, so there it
 * does not render at all.
 */

const SEEN_KEY = 'krevya.landing.guideSeen';
const AUTO_CLOSE_MS = 7000;

const STEPS: { selector: string; text: [string, string] }[] = [
  {
    selector: '.lp-gallery',
    text: [
      'I’m Magix. Krevya reads your product and finds angles worth testing.',
      'Ben Magix. Krevya ürününü okur, test etmeye değer açıları bulur.',
    ],
  },
  {
    selector: '#store',
    text: [
      'The ad opens a page that says the same thing. Krevya builds that too.',
      'Reklamın açtığı sayfa da aynı şeyi söyler. Onu da Krevya hazırlar.',
    ],
  },
  {
    selector: '#how',
    text: [
      'Four steps from catalogue to campaign. This is how it goes.',
      'Katalogdan kampanyaya dört adım. İşte böyle gidiyor.',
    ],
  },
  {
    selector: '#pricing',
    text: [
      'One subscription, every tool. Pick the size that fits your store.',
      'Tek abonelik, bütün araçlar. Mağazana uyan boyu seç.',
    ],
  },
  {
    selector: '#contact',
    text: [
      'Questions? Umut built this and picks up the phone.',
      'Sorun mu var? Bunu Umut yaptı, telefonu da kendisi açar.',
    ],
  },
];

function alreadySeen(): boolean {
  try {
    return preferenceStorage().getItem(SEEN_KEY) === '1';
  } catch {
    // Storage blocked: treat it as seen rather than bring the guide back on
    // every page view for someone who cannot be remembered.
    return true;
  }
}

function markSeen(): void {
  try {
    preferenceStorage().setItem(SEEN_KEY, '1');
  } catch {
    // Nothing to do, and nothing worth telling the visitor about.
  }
}

/** Enough of the element is on screen to count as "here", also for one taller than the screen. */
const isHere = (e: IntersectionObserverEntry) =>
  e.isIntersecting && (e.intersectionRatio >= 0.3 || e.intersectionRect.height >= window.innerHeight * 0.5);

export function LandingGuide() {
  const t = useT();
  const { locale } = useLocale();
  const tr = locale === 'tr';
  const tx = (en: string, turkish: string) => (tr ? turkish : en);

  const hydrated = useHydrated();
  const [wide, setWide] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 901px)').matches,
  );
  const [gone, setGone] = useState(alreadySeen);
  const [heroVisible, setHeroVisible] = useState(true);
  const [footerVisible, setFooterVisible] = useState(false);
  const [step, setStep] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(0);
  const last = useRef(-1);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 901px)');
    const onChange = () => setWide(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Not while the prerendered page is adopted: the build cannot know the
  // width of this screen or whether the guide was dismissed before.
  const active = hydrated && wide && !gone;
  useEffect(() => {
    if (!active) return;
    const hero = document.querySelector('.lp-hero');
    // The footer's links sit where the guide's corner is, so it steps aside for them.
    const footer = document.querySelector('.lp-footer');
    const nodes = STEPS.map((s) => document.querySelector(s.selector));
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === footer) {
            setFooterVisible(entry.isIntersecting);
            continue;
          }
          const here = isHere(entry);
          if (entry.target === hero) {
            setHeroVisible(here);
            continue;
          }
          const i = nodes.indexOf(entry.target);
          // Forward only: scrolling back up to a section it has already
          // spoken about does not make it speak again.
          if (here && i > last.current) {
            last.current = i;
            setStep(i);
            setOpen(true);
            window.clearTimeout(closeTimer.current);
            closeTimer.current = window.setTimeout(() => setOpen(false), AUTO_CLOSE_MS);
          }
        }
      },
      { threshold: [0, 0.3] },
    );
    if (hero) io.observe(hero);
    if (footer) io.observe(footer);
    nodes.forEach((n) => n && io.observe(n));
    return () => {
      io.disconnect();
      window.clearTimeout(closeTimer.current);
    };
  }, [active]);

  // A sentence is about the section it appeared for: once the reader has
  // scrolled most of a screen on, it folds away instead of riding over the
  // next section's text.
  useEffect(() => {
    if (!active || !open) return;
    const from = window.scrollY;
    // Once a frame: `scrollY` read inside the scroll event restyles the page on the spot.
    let frame = 0;
    const read = () => {
      frame = 0;
      if (Math.abs(window.scrollY - from) > window.innerHeight * 0.6) setOpen(false);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(read);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [active, open, step]);

  const toggle = useCallback(() => {
    window.clearTimeout(closeTimer.current);
    setOpen((v) => !v);
  }, []);
  const dismiss = useCallback(() => {
    markSeen();
    setGone(true);
  }, []);

  if (!active) return null;

  return (
    <aside className="lp-guide" data-open={open ? 'true' : undefined} hidden={heroVisible || footerVisible || step === null}>
      <div className="lp-guide-bubble">
        <p>{STEPS[step ?? 0]!.text[tr ? 1 : 0]}</p>
        <button type="button" className="lp-guide-close" onClick={dismiss} aria-label={t('landing.nav.close')}>
          <LandingIcon name="cross" />
        </button>
      </div>
      <button
        type="button"
        className="lp-guide-face"
        onClick={toggle}
        aria-label={tx('Magix, the guide', 'Rehber Magix')}
        aria-expanded={open}
      >
        <Mascot3D />
      </button>
    </aside>
  );
}
